import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  deleteEditableLayer,
  listEditableLayers,
  getEditableLayer,
  mergeEditableLayerFile,
  saveEditableLayer,
  uploadEditableLayer,
} from "./api";
import type { EditableLayerSummary, GeoJsonFeatureCollection } from "./types";

/** A layer's geometry, once it has been fetched. */
type LayerData = Record<number, GeoJsonFeatureCollection>;

export type EditableLayersState = ReturnType<typeof useEditableLayers>;

/**
 * Editable layers as the OFFICE sees them: upload, show, hide, delete.
 *
 * **The geometry is edited in the field app, not here.** The portal manages the
 * file -- puts it on the map, takes it off, or throws it away -- and a
 * surveyor standing at the thing being mapped is the
 * one who moves a road that is in the wrong place. Editing from a desk, against
 * an orthomosaic that may be months old, is how a correct line gets "corrected"
 * into a wrong one by someone who cannot see the ground.
 *
 * The backend still exposes PUT /projects/{id}/editable-layers/{layer} for
 * exactly that reason -- the field app is its client.
 *
 * **Geometry is fetched per layer, on demand.** The listing endpoint returns no
 * coordinates at all -- a project can hold several layers of tens of thousands
 * of features, and loading all of them to render a list of names would be
 * pointless. A layer's collection arrives the first time it is switched on and
 * is then kept, so toggling it off and on again is instant.
 */
export function useEditableLayers(projectId: number) {
  const [layers, setLayers] = useState<EditableLayerSummary[] | null>(null);
  const [data, setData] = useState<LayerData>({});
  const [visible, setVisible] = useState<Record<number, boolean>>({});
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Latest data without making every callback depend on it -- the upload and
  // toggle paths read it to decide whether a fetch is needed, and rebuilding
  // those callbacks on every geometry change would re-run the map's effects.
  const dataRef = useRef<LayerData>({});
  dataRef.current = data;

  // Same trick for the visibility map, read by the default-on effect below to
  // tell a layer it has never seen from one deliberately switched off.
  const visibleRef = useRef<Record<number, boolean>>({});
  visibleRef.current = visible;

  const reload = useCallback(async () => {
    try {
      const list = await listEditableLayers(projectId);
      setLayers(list);
      return list;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load layers.");
      setLayers([]);
      return [];
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * Fetches a layer's geometry unless it is already held, and returns it
   * either way -- callers that need the collection right after calling this
   * (the attribute-table view, GeoJSON export) cannot just re-read `data`
   * afterward: `data` in their own closure is whatever it was when that
   * closure was created, and awaiting this does not make React re-hand them
   * a fresher one. dataRef sidesteps the same problem internally (kept in
   * sync every render, unlike a closed-over `data`), so this reads from
   * dataRef rather than the `data` state variable in this hook's own scope.
   */
  const ensureLoaded = useCallback(
    async (layerId: number) => {
      if (dataRef.current[layerId]) return dataRef.current[layerId];
      setLoadingIds((s) => new Set(s).add(layerId));
      try {
        const detail = await getEditableLayer(projectId, layerId);
        setData((d) => ({ ...d, [layerId]: detail.geojson }));
        return detail.geojson;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Couldn't load that layer.");
        // Switched back off, so the toggle doesn't sit on for a layer that
        // never arrived -- otherwise it reads as an empty layer rather than a
        // failed fetch.
        setVisible((v) => ({ ...v, [layerId]: false }));
        return undefined;
      } finally {
        setLoadingIds((s) => {
          const n = new Set(s);
          n.delete(layerId);
          return n;
        });
      }
    },
    [projectId]
  );

  /** Fetches a layer's geometry on demand without switching it on on the map --
   *  the attribute-table view and GeoJSON export both need the full
   *  FeatureCollection but have nothing to do with what's drawn. Exposes
   *  ensureLoaded under a name that makes sense outside this file. */
  const loadLayer = ensureLoaded;

  /**
   * Every layer starts switched ON, so a project's overlays are simply there
   * when the workspace opens rather than waiting to be found and toggled.
   *
   * This partly walks back the on-demand loading described above: each layer
   * defaulting to visible means each layer's geometry is fetched on load, so
   * a project with several large layers now pays that cost up front. Kept
   * deliberately anyway -- an overlay nobody switched on is an overlay nobody
   * knows is there, which was the actual complaint. The fetches still run
   * per layer through ensureLoaded, so rows fill in independently and one
   * slow layer doesn't hold up the rest.
   *
   * Keyed on whether the id has been SEEN, not on whether it is true: a layer
   * the operator switched off has `false` recorded and must stay off, while a
   * newly uploaded one (already set true by upload) is left alone too.
   */
  useEffect(() => {
    if (!layers) return;
    const unseen = layers.filter((l) => !(l.id in visibleRef.current));
    if (unseen.length === 0) return;
    setVisible((v) => {
      const next = { ...v };
      for (const l of unseen) next[l.id] = true;
      return next;
    });
    for (const l of unseen) void ensureLoaded(l.id);
  }, [layers, ensureLoaded]);

  const toggleVisible = useCallback(
    (layerId: number) => {
      setVisible((v) => {
        const next = !v[layerId];
        if (next) void ensureLoaded(layerId);
        return { ...v, [layerId]: next };
      });
    },
    [ensureLoaded]
  );

  /**
   * Writes an edited collection back, for the attribute table.
   *
   * The endpoint replaces the whole FeatureCollection -- there is no
   * per-feature route -- so this sends what the caller hands it, geometry and
   * all. That is fine precisely because the table only ever rewrites
   * `properties`: the shapes in the collection are the ones just read back
   * from the server, passed through untouched.
   */
  const saveLayer = useCallback(
    async (layerId: number, collection: GeoJsonFeatureCollection) => {
      setBusy(true);
      setError(null);
      try {
        const detail = await saveEditableLayer(projectId, layerId, collection);
        setData((d) => ({ ...d, [layerId]: detail.geojson }));
        await reload();
      } finally {
        setBusy(false);
      }
    },
    [projectId, reload]
  );

  /**
   * Updates a layer from a new version of its file. A preview (apply false)
   * changes nothing; after a real update the held geometry is dropped and
   * fetched again, so the map shows the new shapes.
   */
  const mergeFile = useCallback(
    async (layerId: number, file: File, matchKey: string | null, apply: boolean) => {
      const result = await mergeEditableLayerFile(projectId, layerId, file, { matchKey, apply });
      if (apply) {
        setData((d) => {
          const n = { ...d };
          delete n[layerId];
          return n;
        });
        delete dataRef.current[layerId];
        await reload();
        if (visibleRef.current[layerId]) await ensureLoaded(layerId);
      }
      return result;
    },
    [projectId, reload, ensureLoaded]
  );

  const remove = useCallback(
    async (layerId: number) => {
      setError(null);
      try {
        await deleteEditableLayer(projectId, layerId);
        setData((d) => {
          const n = { ...d };
          delete n[layerId];
          return n;
        });
        setVisible((v) => {
          const n = { ...v };
          delete n[layerId];
          return n;
        });
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Couldn't delete that layer.");
      }
    },
    [projectId, reload]
  );

  const upload = useCallback(
    async (file: File) => {
      setError(null);
      const detail = await uploadEditableLayer(projectId, file);
      setData((d) => ({ ...d, [detail.id]: detail.geojson }));
      // On by default: someone who just uploaded a layer wants to see it, and
      // an upload that appears to do nothing is the most common way this kind
      // of panel reads as broken.
      setVisible((v) => ({ ...v, [detail.id]: true }));
      await reload();
      return detail;
    },
    [projectId, reload]
  );

  /** What the map should draw: every visible layer whose geometry has arrived. */
  const drawn = useMemo(
    () =>
      (layers ?? [])
        .filter((l) => visible[l.id] && data[l.id])
        .map((l) => ({
          id: l.id,
          name: l.name,
          collection: data[l.id],
        })),
    [layers, visible, data]
  );

  return {
    layers,
    data,
    visible,
    loadingIds,
    busy,
    error,
    setError,
    drawn,
    reload,
    loadLayer,
    saveLayer,
    toggleVisible,
    mergeFile,
    remove,
    upload,
  };
}
